/**
 * Supplier Response Form - CR-1403
 *
 * Form for suppliers to submit their RFQ responses:
 * - NDA acceptance (if required)
 * - Line item pricing
 * - Additional costs entry
 * - Certificate upload
 * - Notes and validity period
 *
 * All numerics coming from the API are coerced with `toNumber` — DRF sends
 * decimals as strings. Once the RFQ is awarded / cancelled, the deadline has
 * passed, or the response is already submitted, the form is read-only.
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Send, Save, Loader2, Plus, Trash2, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQ, SupplierResponse, RFQCostType, RFQNDA } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import NDAAcceptance from './NDAAcceptance';
import SupplierResponseLineItems from './SupplierResponseLineItems';
import type { LineItemResponse } from './SupplierResponseLineItems';
import SupplierResponseCertificates from './SupplierResponseCertificates';
import { toNumber, toNullableNumber } from './rfqFormat';
import { getResponseEditability } from './rfqResponseState';
import { useRfqMoney } from './useRfqMoney';

interface AdditionalCostResponse {
  cost_type: RFQCostType;
  description: string;
  amount: number;
}

interface SupplierResponseFormProps {
  rfq: RFQ;
  response: SupplierResponse;
  onSuccess?: () => void;
  onClose?: () => void;
}

const COST_TYPES: { value: RFQCostType; label: string }[] = [
  { value: 'tooling', label: 'Tooling' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'transport', label: 'Transport' },
  { value: 'setup', label: 'Setup' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Other' },
];

export default function SupplierResponseForm({
  rfq,
  response,
  onSuccess,
  onClose
}: SupplierResponseFormProps) {
  const { formatDate } = useFormatters();
  const formatMoney = useRfqMoney(rfq.currency);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(response.nda_accepted);
  const [showNDA, setShowNDA] = useState(rfq.nda_required && !response.nda_accepted);
  // The supplier's own RFQNDA record. NDAAcceptance needs it for the
  // document to review and for the id its accept/decline endpoints take —
  // it used to be rendered with neither, so the supplier could never see the
  // NDA, and the old accept call posted the SupplierResponse id to the NDA
  // endpoint (a different table).
  const [nda, setNda] = useState<RFQNDA | null>(null);
  const [ndaLoading, setNdaLoading] = useState(false);
  const [ndaRecordMissing, setNdaRecordMissing] = useState(false);

  // Response details
  const [validityDays, setValidityDays] = useState(response.validity_period_days || 30);
  const [leadTimeDays, setLeadTimeDays] = useState(response.lead_time_days || 14);
  const [paymentTerms, setPaymentTerms] = useState(response.payment_terms || '');
  const [deliveryTerms, setDeliveryTerms] = useState(response.delivery_terms || '');
  const [notes, setNotes] = useState(response.notes || '');

  const [lineResponses, setLineResponses] = useState<LineItemResponse[]>([]);
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCostResponse[]>([]);
  // True once a line or cost has been edited but not yet saved. The seeding
  // effect below re-runs whenever the parent refetches the response — which
  // a certificate upload does — and used to overwrite what had just been
  // typed with the server's still-empty lines. A ref, not state: flipping
  // it must not itself re-run that effect.
  const dirtyRef = useRef(false);

  const { editable, reason: readOnlyReason } = getResponseEditability(rfq, response);
  // The revision round still waiting for this supplier's resubmission, if any.
  const openRevision = response.response_status === 'revision_requested'
    ? (response.revisions ?? []).find((r) => !r.resubmitted_at) ?? (response.revisions ?? [])[0] ?? null
    : null;

  useEffect(() => {
    if (dirtyRef.current) return;
    if (rfq.line_items) {
      setLineResponses(rfq.line_items.map(item => {
        const existingLine = response.line_items?.find(l => l.rfq_line_item === item.id);
        return {
          rfq_line_item: item.id,
          line_number: item.line_number,
          item_description: item.item_description,
          quantity: toNumber(item.quantity),
          unit_of_measure: item.unit_of_measure,
          target_unit_price: toNullableNumber(item.target_unit_price),
          unit_price: toNumber(existingLine?.unit_price),
          lead_time_days: toNullableNumber(existingLine?.lead_time_days),
          quoted_uom: existingLine?.quoted_uom || '',
          notes: existingLine?.notes || '',
        };
      }));
    }
    if (response.additional_costs) {
      setAdditionalCosts(response.additional_costs.map(cost => ({
        cost_type: cost.cost_type,
        description: cost.description,
        amount: toNumber(cost.amount),
      })));
    }
  }, [rfq, response]);

  useEffect(() => {
    if (!showNDA) return;
    let cancelled = false;
    setNdaLoading(true);
    // The NDA viewset is already scoped to the caller's own supplier, so
    // filtering by rfq is enough to get this supplier's record.
    rfqAPI.getNDAs({ rfq: rfq.id })
      .then((res) => {
        if (cancelled) return;
        const record = (res.data.results || []).find((n) => n.supplier === response.supplier) ?? res.data.results?.[0] ?? null;
        setNda(record);
        setNdaRecordMissing(!record);
        if (record?.status === 'accepted') {
          setNdaAccepted(true);
          setShowNDA(false);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Error loading NDA:', error);
        setNdaRecordMissing(true);
      })
      .finally(() => { if (!cancelled) setNdaLoading(false); });
    return () => { cancelled = true; };
  }, [showNDA, rfq.id, response.supplier]);

  // NDAAcceptance performs the accept/decline API call itself (it holds the
  // RFQNDA id); these callbacks only move this form on afterwards.
  const handleNDAAccept = async () => {
    setNda((prev) => (prev ? { ...prev, status: 'accepted' } : prev));
    setNdaAccepted(true);
    setShowNDA(false);
    onSuccess?.();
  };

  const handleNDADecline = async () => {
    setNda((prev) => (prev ? { ...prev, status: 'declined' } : prev));
    toast.info('NDA declined. You cannot respond to this RFQ.');
    onClose?.();
  };

  const updateLineResponse = (index: number, field: keyof LineItemResponse, value: number | string | null) => {
    dirtyRef.current = true;
    setLineResponses(prev => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  };

  const addAdditionalCost = () => {
    dirtyRef.current = true;
    setAdditionalCosts([...additionalCosts, { cost_type: 'tooling', description: '', amount: 0 }]);
  };

  const removeAdditionalCost = (index: number) => {
    dirtyRef.current = true;
    setAdditionalCosts(additionalCosts.filter((_, i) => i !== index));
  };

  const updateAdditionalCost = (index: number, field: keyof AdditionalCostResponse, value: string | number) => {
    dirtyRef.current = true;
    setAdditionalCosts(prev => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const lineItemsTotal = lineResponses.reduce((sum, item) => sum + toNumber(item.unit_price) * toNumber(item.quantity), 0);
  const costsTotal = additionalCosts.reduce((sum, cost) => sum + toNumber(cost.amount), 0);
  const grandTotal = lineItemsTotal + costsTotal;

  const persist = async () => {
    await rfqAPI.updateResponse(response.id, {
      validity_period_days: validityDays,
      lead_time_days: leadTimeDays,
      payment_terms: paymentTerms,
      delivery_terms: deliveryTerms,
      notes: notes,
    });

    for (const lineResp of lineResponses) {
      const existingLine = response.line_items?.find(l => l.rfq_line_item === lineResp.rfq_line_item);
      const payload = {
        unit_price: lineResp.unit_price,
        lead_time_days: lineResp.lead_time_days ?? undefined,
        quoted_uom: lineResp.quoted_uom.trim(),
        notes: lineResp.notes,
      };
      if (existingLine) {
        await rfqAPI.updateResponseLineItem(existingLine.id, payload);
      } else {
        await rfqAPI.createResponseLineItem({ response: response.id, rfq_line_item: lineResp.rfq_line_item, ...payload });
      }
    }

    // Delete existing costs and recreate (simpler than tracking changes)
    for (const cost of response.additional_costs ?? []) {
      await rfqAPI.deleteResponseAdditionalCost(cost.id);
    }
    for (const cost of additionalCosts) {
      await rfqAPI.createResponseAdditionalCost({
        response: response.id,
        cost_type: cost.cost_type,
        description: cost.description,
        amount: cost.amount,
        currency: rfq.currency,
      });
    }
    // Everything on screen is now on the server, so the next refetch may
    // seed the form again.
    dirtyRef.current = false;
  };

  const handleSave = async () => {
    if (!editable) return;
    try {
      setLoading(true);
      await persist();
      toast.success('Response saved');
      onSuccess?.();
    } catch (error) {
      console.error('Error saving response:', error);
      toast.error('Failed to save response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!editable) return;
    if (lineResponses.some(l => toNumber(l.unit_price) <= 0)) {
      toast.error('Please provide prices for all line items');
      return;
    }
    try {
      setSubmitting(true);
      await persist();
      await rfqAPI.submitResponse(response.id);
      toast.success(openRevision ? 'Revised quote submitted' : 'Response submitted successfully');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (showNDA) {
    return (
      <NDAAcceptance
        nda={nda ?? undefined}
        document={nda?.document_details ?? undefined}
        recordMissing={!ndaLoading && ndaRecordMissing}
        onAccept={handleNDAAccept}
        onDecline={handleNDADecline}
        loading={loading || ndaLoading}
      />
    );
  }

  const disabled = !editable || loading || submitting;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{editable ? 'Submit Response' : 'Your Response'}</h2>
          <p className="text-muted-foreground">RFQ: {rfq.rfq_number} - {rfq.title}</p>
        </div>
        {ndaAccepted && (
          <Badge variant="outline" className="text-green-600 border-green-600">
            <Check className="h-3 w-3 mr-1" /> NDA Accepted
          </Badge>
        )}
      </div>

      {!editable && readOnlyReason && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm">{readOnlyReason}</span>
          </CardContent>
        </Card>
      )}

      {/* Negotiation: the buyer asked for a revised quote. Their ask and any
          targets sit above the form so the supplier knows what to move. */}
      {openRevision && (
        <Card className="border-purple-200 bg-purple-50" data-testid="revision-request-banner">
          <CardContent className="pt-4 space-y-1">
            <div className="flex items-center gap-2 font-medium text-purple-900">
              <AlertTriangle className="h-5 w-5 text-purple-600" />
              Revised quote requested (round {openRevision.round_number})
              {openRevision.requested_by_name ? ` by ${openRevision.requested_by_name}` : ''}
              {openRevision.requested_at ? ` on ${formatDate(openRevision.requested_at)}` : ''}
            </div>
            <p className="text-sm whitespace-pre-line">{openRevision.message}</p>
            {(openRevision.target_total || openRevision.target_lead_time_days !== null) && (
              <p className="text-xs text-purple-900">
                {openRevision.target_total ? `Target total: ${formatMoney(openRevision.target_total)}` : ''}
                {openRevision.target_total && openRevision.target_lead_time_days !== null ? ' · ' : ''}
                {openRevision.target_lead_time_days !== null ? `Target lead time: ${openRevision.target_lead_time_days} days` : ''}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Your previous quote ({formatMoney(openRevision.previous_quote?.grand_total ?? 0)}) is kept on record. Update the prices below and submit again.
            </p>
          </CardContent>
        </Card>
      )}

      <SupplierResponseLineItems
        lines={lineResponses}
        currency={rfq.currency}
        disabled={disabled}
        formatMoney={formatMoney}
        onUpdate={updateLineResponse}
      />

      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Additional Costs</CardTitle>
              <CardDescription>Tooling, packaging, transport, etc.</CardDescription>
            </div>
            {editable && (
              <Button type="button" variant="outline" size="sm" onClick={addAdditionalCost} disabled={disabled}>
                <Plus className="h-4 w-4 mr-1" /> Add Cost
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {additionalCosts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {editable ? 'No additional costs. Click "Add Cost" if applicable.' : 'No additional costs.'}
            </p>
          ) : (
            <div className="space-y-3">
              {additionalCosts.map((cost, index) => (
                <div key={index} className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={cost.cost_type}
                      onChange={(e) => updateAdditionalCost(index, 'cost_type', e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                      disabled={disabled}
                    >
                      {COST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={cost.description}
                      onChange={(e) => updateAdditionalCost(index, 'description', e.target.value)}
                      placeholder="Description"
                      disabled={disabled}
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Amount ({rfq.currency})</Label>
                      <Input
                        type="number"
                        value={cost.amount || ''}
                        onChange={(e) => updateAdditionalCost(index, 'amount', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        disabled={disabled}
                      />
                    </div>
                    {editable && (
                      <Button variant="ghost" size="sm" className="mt-5" onClick={() => removeAdditionalCost(index)} disabled={disabled}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierResponseCertificates
        responseId={response.id}
        certificates={rfq.required_certificates ?? []}
        submissions={response.certificate_submissions ?? []}
        disabled={disabled}
        formatDate={(d) => formatDate(d)}
        onUploaded={() => onSuccess?.()}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Response Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label>Quote Validity (days)</Label>
            <Input type="number" value={validityDays} onChange={(e) => setValidityDays(parseInt(e.target.value, 10) || 30)} min={1} disabled={disabled} />
          </div>
          <div>
            <Label>Overall Lead Time (days)</Label>
            <Input type="number" value={leadTimeDays} onChange={(e) => setLeadTimeDays(parseInt(e.target.value, 10) || 14)} min={0} disabled={disabled} />
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="e.g., Net 30 days" disabled={disabled} />
          </div>
          <div>
            <Label>Delivery Terms</Label>
            <Input value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)} placeholder="e.g., DDP, EXW" disabled={disabled} />
          </div>
          <div className="col-span-2">
            <Label>Additional Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional information or conditions..." rows={3} disabled={disabled} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-muted/50">
        <CardContent className="pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Line Items Total:</span>
            <span>{formatMoney(lineItemsTotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Additional Costs:</span>
            <span>{formatMoney(costsTotal)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 border-t">
            <span>Grand Total:</span>
            <span>{formatMoney(grandTotal)}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          {editable ? 'Cancel' : 'Close'}
        </Button>
        {editable && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSave} disabled={disabled}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={disabled}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" />
              Submit Response
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
